import { useCallback, useEffect, useRef, useState } from "react";

export function useResource<T>(
  loader: (signal: AbortSignal) => Promise<T>,
  dependencies: readonly unknown[],
  intervalMs?: number,
) {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  const refresh = useCallback(async (silent = false, externalSignal?: AbortSignal) => {
    const controller = new AbortController();
    const abort = () => controller.abort();
    externalSignal?.addEventListener("abort", abort, { once: true });
    if (!silent) setLoading(true);
    try {
      const value = await loaderRef.current(controller.signal);
      setData(value);
      setError(null);
    } catch (caught) {
      if (!controller.signal.aborted) setError(caught instanceof Error ? caught : new Error(String(caught)));
    } finally {
      externalSignal?.removeEventListener("abort", abort);
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void refresh(false, controller.signal);
    const timer = intervalMs ? window.setInterval(() => void refresh(true), intervalMs) : undefined;
    return () => {
      controller.abort();
      if (timer) window.clearInterval(timer);
    };
    // Resource dependencies are intentionally controlled by each page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, intervalMs, refresh]);

  return { data, loading, error, refresh: () => refresh(false), setData };
}
